// ─── ThemeContext ──────────────────────────────────────────────────────────────
// Single source of truth for theme state.
// All pages import useTheme from here — never from App.jsx.
//
// Usage:
//   import { useTheme } from "../lib/ThemeContext.jsx";
//   const T = useTheme();

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { resolveTheme, getInitialMode } from "./theme.js";
import { LS } from "./storage.js";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(getInitialMode);
  const [theme, setTheme] = useState(() => resolveTheme(getInitialMode()));

  // Re-resolve when mode changes or (for "auto") when the hour boundary crosses
  useEffect(() => {
    function update() {
      const resolved = resolveTheme(mode);
      setTheme(resolved);
    }
    update();

    // For auto mode, re-check every minute so the theme flips at 08:00 / 20:00
    if (mode === "auto") {
      const id = setInterval(update, 60_000);
      return () => clearInterval(id);
    }
  }, [mode]);

  const setMode = useCallback((m) => {
    setModeState(m);
    LS.set(LS.THEME_MODE, m);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, mode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Returns the current resolved theme object (same shape as THEMES.dark / THEMES.light). */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Graceful fallback for components rendered outside the provider (e.g. tests)
    return resolveTheme("auto");
  }
  return ctx.theme;
}

/** Returns { mode, setMode } for the theme toggle in Settings. */
export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used inside <ThemeProvider>");
  return { mode: ctx.mode, setMode: ctx.setMode };
}
